import { useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { DesignAvatar, getRandomAvatarUrl } from '@components/design-avatar';
import { EmptyStateCell } from '@components/empty-state';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAiAgent } from '@components/icons/ai-agent';
import { Spinner } from '@components/loading-spinner/loading-spinner';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { AIAgent } from 'api/ai-agents/requests';

import {
  ActionItem,
  GroupHeader,
  OnCreateActionSelectProps,
  SearchEmptyState,
  TabContentWrapper,
} from './common';

type AIAgentsTabProps = {
  aiAgents: AIAgent[] | undefined;
  isLoading: boolean;
  onSelect: (props: OnCreateActionSelectProps) => void;
  searchQuery?: string;
  onClearSearch: () => void;
};

export const AIAgentsTab = ({
  aiAgents,
  isLoading,
  onSelect,
  searchQuery,
  onClearSearch,
}: AIAgentsTabProps) => {
  const { t } = useScopedTranslate(locale);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const hasNoSearchResults = searchQuery && aiAgents?.length === 0;

  return (
    <TabContentWrapper>
      {isLoading ? (
        <Spinner />
      ) : hasNoSearchResults ? (
        <SearchEmptyState onClearSearch={onClearSearch} />
      ) : (
        <>
          <AgentWrapper>
            <ActionItem
              text={t('addAgent')}
              description={t('addAgentDescription')}
              icon={<IconAddPlus color="aiGradientTeal100" />}
              type="ask_ai_agent"
              onSelect={() =>
                onSelect({
                  actionType: 'ask_ai_agent',
                  values: { picture: getRandomAvatarUrl() },
                })
              }
            />
            <Stack
              space={4}
              orientation="horizontal"
              justify="space-between"
              align="center"
              style={{ width: '100%' }}
            >
              <Separator />
              <Text
                font="semibold14"
                style={{ textTransform: 'uppercase' }}
                text={t('core.or')}
                color="textHelper"
              />
              <Separator />
            </Stack>
            <GroupHeader
              onClick={() => setIsCollapsed(!isCollapsed)}
              isCollapsed={isCollapsed}
              text={t('duplicateFromTemplates')}
              textFont="semibold14"
            />
            <Collapse isCollapsed={isCollapsed}>
              <div>
                {aiAgents && aiAgents.length > 0 ? (
                  aiAgents.map((agent) => (
                    <ActionItem
                      key={agent.shortId}
                      text={agent.name}
                      description={agent.description}
                      icon={<DesignAvatar size={24} src={agent.picture} />}
                      type="ask_ai_agent"
                      onSelect={() =>
                        onSelect({
                          actionType: 'ask_ai_agent',
                          values: { aiAgentId: agent.shortId },
                        })
                      }
                    />
                  ))
                ) : (
                  <div style={{ padding: pxToRem(16) }}>
                    <EmptyStateCell icon={<IconAiAgent />} text={t('noAiAgents')} />
                  </div>
                )}
              </div>
            </Collapse>
          </AgentWrapper>
        </>
      )}
    </TabContentWrapper>
  );
};

const AgentWrapper = styled.div({
  '.action-item': {
    padding: pxToRem(8),
  },
});

const locale = {
  en: {
    addAgent: 'Add agent',
    addAgentDescription: 'Create a new agent for this journey',
    noAiAgents: 'Your company does not have any AI Agent templates',
    duplicateFromTemplates: 'Duplicate from AI Agent templates',
  },
};
